import { apiSlice } from "../../app/api/apiSlice";

const QUERY_URL = "/member";

export const memberApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMembers: builder.query({
      query: () => QUERY_URL,
      // keepUnusedDataFor: 5,
      providesTags: (result, error, arg) =>
        result
          ? [...result.map(({ id }) => ({ type: "Member", id })), "Member"]
          : ["Member"],
    }),
    getMemberSelf: builder.query({
      query: () => `${QUERY_URL}/me`,
      providesTags: ["MemberSelf"],
    }),
    getMemberSelfSite: builder.query({
      query: () => `${QUERY_URL}/me/site`,
      providesTags: ["MemberSelfSite"],
    }),
    addNewMember: builder.mutation({
      query: (member) => ({
        url: QUERY_URL,
        method: "POST",
        body: { ...member },
      }),
      invalidatesTags: ["Member"],
    }),
    updateMember: builder.mutation({
      query: (data) => ({
        url: QUERY_URL,
        method: "PATCH",
        body: { ...data },
      }),
      invalidatesTags: ["Member", "Contract"],
    }),
    updateMemberSelf: builder.mutation({
      query: (data) => ({
        url: `${QUERY_URL}/me`,
        method: "PATCH",
        body: { ...data },
      }),
      invalidatesTags: ["MemberSelf"],
    }),
    deleteMember: builder.mutation({
      query: (data) => ({
        url: QUERY_URL,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["Member"],
    }),
    deleteAllMembers: builder.mutation({
      query: (data) => ({
        url: "/member/deleteall",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Member"],
    }),
    importMembers: builder.mutation({
      query: (uploadData) => ({
        url: "/member/import",
        method: "POST",
        body: uploadData,
      }),
      invalidatesTags: ["Member"],
    }),
    bulkEditMembers: builder.mutation({
      query: ({ memberIds, updateFields }) => ({
        url: "/member/bulkedit",
        method: "POST",
        body: {
          memberIds,
          updateFields,
        },
      }),
      invalidatesTags: ["Member"],
    }),
    bulkMarkPaid: builder.mutation({
      query: ({ memberIds, payedTill, regMemberIds }) => ({
        url: "/member/mark-paid",
        method: "POST",
        body: { memberIds, payedTill, regMemberIds }, // payedTill: "MM/YYYY"
      }),
      invalidatesTags: ["Member"],
    }),
    bulkResetPaid: builder.mutation({
      query: ({
        scope = "ALL",
        siteIds = [],
        memberIds = [],
        onlyActive = true,
        setTo = null,
      }) => ({
        url: "/member/reset-paid",
        method: "POST",
        body: { scope, siteIds, memberIds, onlyActive, setTo }, // setTo: null OR "MM/YYYY"
      }),
      invalidatesTags: ["Member"],
    }),
    sendTerminationMail: builder.mutation({
      query: ({ memberId, terminationData }) => ({
        url: "/member/send-termination-mail",
        method: "POST",
        body: { memberId, terminationData },
      }),
    }),
    sendRegistrationLinks: builder.mutation({
      query: ({ memberIds, validityHours, neverExpires }) => ({
        url: "/member/send-registration-links",
        method: "POST",
        body: { memberIds, validityHours, neverExpires },
      }),
    }),
    rejectSelfRegistration: builder.mutation({
      query: ({ contractId, reason }) => ({
        url: `/member/self-reg-reject/${contractId}`,
        method: "POST",
        body: { reason: reason || "" },
      }),
      invalidatesTags: ["Member"],
    }),
    terminateMemberSelf: builder.mutation({
      query: ({ memberId, terminationDate, lastMonth, contractEndDate }) => ({
        url: `/member/me/terminate/${memberId}`,
        method: "POST",
        body: { terminationDate, lastMonth, contractEndDate },
      }),
      invalidatesTags: ["MemberSelf"],
    }),
    reactivateMemberSelf: builder.mutation({
      query: ({ memberId }) => ({
        url: `/member/me/reactivate/${memberId}`,
        method: "POST",
      }),
      invalidatesTags: ["MemberSelf"],
    }),
    getMemberSitePricingInfo: builder.query({
      query: (siteId) => `/public/site/${siteId}`,
    }),
    addMemberToContractSelf: builder.mutation({
      query: (data) => ({
        url: `${QUERY_URL}/me/add-member`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["MemberSelf"],
    }),
  }),
});

export const {
  useGetMembersQuery,
  useGetMemberSelfQuery,
  useGetMemberSelfSiteQuery,
  useAddNewMemberMutation,
  useUpdateMemberMutation,
  useUpdateMemberSelfMutation,
  useDeleteMemberMutation,
  useDeleteAllMembersMutation,
  useImportMembersMutation,
  useBulkEditMembersMutation,
  useBulkMarkPaidMutation,
  useBulkResetPaidMutation,
  useSendTerminationMailMutation,
  useSendRegistrationLinksMutation,
  useRejectSelfRegistrationMutation,
  useTerminateMemberSelfMutation,
  useReactivateMemberSelfMutation,
  useGetMemberSitePricingInfoQuery,
  useAddMemberToContractSelfMutation,
} = memberApiSlice;
